import {
  useEffect, useState, useContext, useMemo,
} from "react";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import MainContext from "../../context/main/mainContext";
import CollapsibleSection from "../../components/CollapsibleSection";
import CompanyInfo from "./CompanyInfo";
import BillingDetails from "./BillingDetails";
import SeatsConfig from "./SeatsConfig";
import EnrichmentConfig from "./EnrichmentConfig";
import DedicatedCsmConfig from "./DedicatedCsmConfig";
import PreviewSidebar from "./PreviewSideBar";
import ActionsBar from "./ActionBar";
import money from "../../utilities/money";
import HeaderBarQuote from "./HeaderBarQuote";

const initialForm = {
  companyName: "",
  billing: {
    clientName: "",
    clientEmail: "",
    customMemo: "",
    interval: "year",
    intervalCount: 1,
    intervalOption: "yearly",
    address: {
      line: "",
      city: "",
      state: "",
      zipcode: "",
      country: "US",
    },
  },
  seats: {
    paid: 5,
    free: 0,
    freeSeatsEnabled: true,
    dollars: 48,
    hasCustomPricing: false,
  },
  enrichment: {
    enabled: false,
    credits: 500,
    dollars: 12,
    hasCustomPricing: false,
    level: "PREMIUM",
    options: {
      hasListEnrichment: true,
      hasFreeListEnrichment: false,
      hasEventIntelligence: true,
    },
  },
  dedicatedCsm: {
    enabled: false,
    hasCustomPricing: false,
    dollars: 1000,
  },
};

export default function CreateQuoteForm() {
  const mainContext = useContext(MainContext);
  const team = mainContext?.team?.team || null;
  const {
    previewSubscription,
    createQuote,
  } = mainContext || {};

  const isUpdateMode = Boolean(team?.stripeSubscription);

  const methods = useForm({
    defaultValues: initialForm,
    mode: "onChange",
  });
  const { control, handleSubmit, reset } = methods;
  const form = useWatch({ control });

  const [isLoading, setIsLoading] = useState(false);
  const [previewInfo, setPreviewInfo] = useState({});
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const [sectionsExpanded, setSectionsExpanded] = useState({
    company: !isUpdateMode,
    billing: !isUpdateMode,
    seats: !isUpdateMode,
    enrichment: !isUpdateMode,
    dedicatedCsm: !isUpdateMode,
  });

  const toggleSection = (section) => {
    setSectionsExpanded((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const payload = useMemo(() => {
    const f = form || {};
    return {
      companyName: f.companyName,
      billing: {
        ...(f.billing?.customMemo && { customMemo: f.billing.customMemo }),
        interval: f.billing?.interval,
        intervalCount: Number(f.billing?.intervalCount) || 1,
        clientName: f.billing?.clientName,
        clientEmail: f.billing?.clientEmail,
      },
      seats: {
        paid: Number(f.seats?.paid) || 0,
        ...(f.seats?.freeSeatsEnabled ? { free: Number(f.seats?.free ?? 0) } : undefined),
        ...(f.seats?.hasCustomPricing && { unitAmount: money.dollarsToCents(f.seats.dollars) }),
      },
      subscriptionTier: f.subscriptionTier || "",
      ...(f.enrichment
        && f.enrichment.enabled && {
        enrichment: {
          credits: Number(f.enrichment.credits) || 0,
          ...(f.enrichment.hasCustomPricing && {
            unitAmount: money.dollarsToCents(f.enrichment.dollars),
          }),
          level: f.enrichment.level || "PREMIUM",
          options: {
            hasListEnrichment: Boolean(f.enrichment.options?.hasListEnrichment),
            hasFreeListEnrichment: Boolean(f.enrichment.options?.hasFreeListEnrichment),
            hasEventIntelligence: Boolean(f.enrichment.options?.hasEventIntelligence),
          },
        },
      }),
      ...(f.dedicatedCsm
        && f.dedicatedCsm.enabled && {
        dedicatedCsm: {
          ...(f.dedicatedCsm.hasCustomPricing && {
            unitAmount: money.dollarsToCents(f.dedicatedCsm.dollars),
          }),
        },
      }),
    };
  }, [form]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoadingPreview(true);
    const timeout = setTimeout(async () => {
      try {
        const preview = await previewSubscription(payload, controller.signal);
        setPreviewInfo(preview);
      } catch (err) {
        if (err.name !== "AbortError") {
          // do nothing
        }
      } finally {
        setIsLoadingPreview(false);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [
    payload.billing?.interval,
    payload.billing?.intervalCount,
    payload.seats,
    payload.enrichment,
    payload.dedicatedCsm,
    payload.billing?.trialExpiresOn,
    team?.id,
  ]);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      await createQuote(payload);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = "mt-1.5 w-full border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";
  const labelClasses = "block text-sm font-medium text-gray-900";
  const helperClasses = "text-xs text-gray-500 mt-1.5";

  const handleReset = () => {
    reset(initialForm);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <HeaderBarQuote />

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <CollapsibleSection
                  title="Company Information"
                  isExpanded={sectionsExpanded.company}
                  onToggle={() => toggleSection("company")}
                >
                  <CompanyInfo
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                    isQuote={true}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Billing Details"
                  isExpanded={sectionsExpanded.billing}
                  onToggle={() => toggleSection("billing")}
                >
                  <BillingDetails
                    isUpdateMode={isUpdateMode}
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                    isQuote={true}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Seat Configuration"
                  isExpanded={sectionsExpanded.seats}
                  onToggle={() => toggleSection("seats")}
                >
                  <SeatsConfig
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Enrichment Configuration"
                  isExpanded={sectionsExpanded.enrichment}
                  onToggle={() => toggleSection("enrichment")}
                >
                  <EnrichmentConfig
                    inputClasses={inputClasses}
                    labelClasses={labelClasses}
                    helperClasses={helperClasses}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  title="Dedicated CSM configuration"
                  isExpanded={sectionsExpanded.dedicatedCsm}
                  onToggle={() => toggleSection("dedicatedCsm")}
                >
                  <DedicatedCsmConfig inputClasses={inputClasses} />
                </CollapsibleSection>

                <ActionsBar isLoading={isLoading} onReset={handleReset} onSubmitText={"Create Quote"} />
              </div>

              <div>
                <PreviewSidebar
                  isLoadingPreview={isLoadingPreview}
                  previewInfo={previewInfo}
                  isUpdateMode={isUpdateMode}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
